// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Damage Reports
// Submit and view own org's damage reports, org auto-filled
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Textarea,
  Select, MultiSelect, Modal,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconRefresh, IconAlertTriangle,
} from '@tabler/icons-react';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMyDamageReports } from '../../../hooks/useMyOrgData';
import { useMainStreetOpsStore } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { OP_STATUS, DAMAGE_TYPES } from '../constants';
import { fmtDate } from '../../../lib/dateFormat';

const OP_SELECT = OP_STATUS.map(s => ({ value: s.id, label: s.label }));
const DAMAGE_SELECT = DAMAGE_TYPES.map(d => ({ value: d.id, label: d.label }));

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'severe', label: 'Severe' },
  { value: 'destroyed', label: 'Destroyed' },
];

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'green', moderate: 'yellow', major: 'orange', severe: 'red', destroyed: 'dark',
};

function opLabel(id: string): string {
  return OP_STATUS.find(s => s.id === id)?.label ?? id;
}

function damageLabel(id: string): string {
  return DAMAGE_TYPES.find(d => d.id === id)?.label ?? id;
}

export default function PortalDamageReport() {
  const myOrg = useMyOrg();
  const myReports = useMyDamageReports();
  const { loading, loadDamageReports, createDamageReport } = useMainStreetOpsStore();
  const { activeIncident, user } = useAuthStore();

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [opStatus, setOpStatus] = useState('');
  const [damageTypes, setDamageTypes] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('');
  const [saving, setSaving] = useState(false);

  const sorted = useMemo(() =>
    [...myReports].sort((a, b) => (b.reportedAt ?? '').localeCompare(a.reportedAt ?? '')),
    [myReports],
  );

  async function handleCreate() {
    if (!opStatus) {
      notifications.show({ color: 'red', title: 'Required', message: 'Operational status is required.' });
      return;
    }
    if (!severity) {
      notifications.show({ color: 'red', title: 'Required', message: 'Severity is required.' });
      return;
    }
    if (!myOrg) return;
    setSaving(true);
    await createDamageReport({
      orgId: String(myOrg.id),
      orgName: myOrg.name,
      incidentId: activeIncident ? String(activeIncident.id) : '',
      opStatus,
      damageTypes: damageTypes.join(','),
      description: description.trim(),
      severity,
      reportedBy: user?.fullName ?? user?.email ?? 'Unknown',
      reportedAt: new Date().toISOString(),
    });
    setSaving(false);
    notifications.show({ color: 'green', title: 'Created', message: 'Damage report submitted.' });
    setOpStatus(''); setDamageTypes([]); setDescription(''); setSeverity('');
    closeCreate();
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">My Damage Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadDamageReports()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Submit Report
          </Button>
        </Group>
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{sorted.length} report{sorted.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Op Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Damage Types</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Reporter</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loading ? 'Loading...' : 'No damage reports'}
                  </td>
                </tr>
              )}
              {sorted.map(report => (
                <tr key={report.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px' }}>
                    {report.opStatus ? (
                      <Badge size="xs" color={OP_STATUS.find(s => s.id === report.opStatus)?.color ?? 'gray'}>
                        {opLabel(report.opStatus)}
                      </Badge>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Group gap={4}>
                      {report.damageTypes
                        ? report.damageTypes.split(',').map(d => (
                            <Badge key={d} size="xs" variant="outline">{damageLabel(d.trim())}</Badge>
                          ))
                        : '-'}
                    </Group>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="xs" color={SEVERITY_COLORS[report.severity] ?? 'gray'}>
                      {report.severity || '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 250 }}>
                    <Text size="xs" lineClamp={2}>{report.description || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{report.reportedBy || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fmtDate(report.reportedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create Modal — org auto-filled */}
      <Modal opened={createOpened} onClose={closeCreate} title="Submit Damage Report" size="lg">
        <Text size="sm" fw={500} mb="sm" c="blue">
          Reporting for: {myOrg?.name}
        </Text>

        <Select label="Operational Status" data={OP_SELECT} value={opStatus}
          onChange={v => setOpStatus(v ?? '')} placeholder="Current status"
          size="sm" mb="sm" required />

        <MultiSelect label="Damage Types" data={DAMAGE_SELECT} value={damageTypes}
          onChange={setDamageTypes} placeholder="Select damage types"
          size="sm" mb="sm" searchable />

        <Select label="Severity" data={SEVERITY_OPTIONS} value={severity}
          onChange={v => setSeverity(v ?? '')} placeholder="Select severity"
          size="sm" mb="sm" required />

        <Textarea label="Description" value={description}
          onChange={e => setDescription(e.currentTarget.value)}
          placeholder="Describe the damage..." size="sm" mb="md" autosize minRows={3} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconAlertTriangle size={16} />}>Submit Report</Button>
        </Group>
      </Modal>
    </div>
  );
}
